import React, { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, AttributionControl } from "react-leaflet";
import StacksMarker from "./StacksMarker";
import { useSelector } from "react-redux";
import useWindowDimensions from "./useWindowDimensions";
import IsLoading from "./IsLoading";
import HasError from "./HasError";

const StacksMap = () => {
  const { screenWidth } = useWindowDimensions();

  // Selectors for data and loading/error states
  const stacks = useSelector((state) => state.stacks);
  const stacksLoading = stacks.stacksLoading;
  const stacksError = stacks.stacksError;
  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationLoading = information.informationLoading;
  const informationError = information.informationError;
  const stackDataOPSGA = stacks?.stacksData?.OPSGA || [];
  const stackDataOPEDA = stacks?.stacksData?.OPEDA || [];

  // Memoized modified stack data to prevent unnecessary re-renders
  const modifiedStackDataOPSGA = useMemo(() => {
    return stackDataOPSGA.map((station) => {
      const specificStationInformation = informationData.find(
        (specificStation) =>
          specificStation.device === "OPSGA" &&
          specificStation.device_id === station.device_id
      );
      return { ...station, ...specificStationInformation };
    });
  }, [stackDataOPSGA, informationData]);

  const modifiedStackDataOPEDA = useMemo(() => {
    return stackDataOPEDA.map((station) => {
      const specificStationInformation = informationData.find(
        (specificStation) =>
          specificStation.device === "OPEDA" &&
          specificStation.device_id === station.device_id
      );
      return { ...station, ...specificStationInformation };
    });
  }, [stackDataOPEDA, informationData]);

  // Combine lists and memoize the result
  const stacksList = useMemo(
    () => [...modifiedStackDataOPSGA, ...modifiedStackDataOPEDA],
    [modifiedStackDataOPSGA, modifiedStackDataOPEDA]
  );

  // Map settings state
  const [mapZoom, setMapZoom] = useState(5.6);
  const [mapHeight, setMapHeight] = useState("90vh");

  // Memoize map settings calculation
  useEffect(() => {
    setMapZoom(screenWidth < 600 ? 2 : 5.6);
    setMapHeight(screenWidth < 600 ? "70vh" : "90vh");
  }, [screenWidth]);

  if (stacksLoading || informationLoading) {
    return <IsLoading />;
  } else if (stacksError || informationError) {
    return <HasError />;
  } else if (Array.isArray(stacksList)) {
    return (
      <div dir="ltr" className="row my-5 mx-md-5">
        <MapContainer
          id="stacksMap"
          center={[32.739, 53.688]}
          zoom={mapZoom}
          zoomSnap={0.1}
          style={{
            width: "90%",
            height: mapHeight,
            margin: "auto",
          }}
          attributionControl={false}
        >
          {stacksList.map((stack) => (
            <React.Fragment key={stack.slug}>
              {stack && stack.is_test === false && (
                <StacksMarker type={stack.device} data={stack} />
              )}
            </React.Fragment>
          ))}

          <TileLayer
            attribution="ایستگاه های فعال کنترل ذرات خروجی دودکش"
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <AttributionControl position="bottomright" prefix={false} />
        </MapContainer>
      </div>
    );
  }
  return null; // Fallback return in case no conditions are met
};

export default StacksMap;
